import React, { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import axios, { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import { IconInfoCircle, Tooltip } from '@trqk/components';
import { locale, getWebpackEnvVar } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { PerformanceVsStatementPeriods } from '../../../../../tooltips';
import { ReportPeriodSelector } from './ReportPeriodSelector';
import { StepWrapper } from '../..';
import {
    useEntities,
    useGapAnalysisTools,
    useToast,
    useTokenModal,
    useStatementSelection,
    usePaymentPlanStatus,
} from '../../../../../../hooks';
import { FULL_PATHS, HASHES } from '../../../../../../constants';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const ADD_GAP_TO_TRQK_USER = `
    mutation addGapToTrqkUser(
        $user_id: String
        $entity_id: String!
        $performance_period_label: String
    ) {
        addGapToTrqkUser(
            user_id: $user_id
            entity_id: $entity_id
            performance_period_label: $performance_period_label
        ) {
            date_created
            description
            detection_batch_status
            entity_id
            gap_analysis_id
            performance_period
            performance_period_end
            performance_period_start
            statement_id_international
            statement_id_standard
            user_id
        }
    }
`;

/**
 * Allows the user to select a report period with <ReportPeriodSelector> in order to perform a Gap
 * Analysis report.
 * @param props - {@link GapAnalysisStepProps}
 */
export const SelectPeriod: GapAnalysisStepComponent = props => {
    const { context, remove, send, stepId } = props;
    const { selectedPeriod } = context;
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const history = useHistory();
    const { selectedEntityId } = useEntities();
    const {
        statementSelectionProps: { hasStatements },
    } = useStatementSelection();
    const { canUploadDetections } = usePaymentPlanStatus();
    const { add: addToast } = useToast();
    const { getTokenAsync } = useTokenModal();
    const { fetchPeriodSelections } = useGapAnalysisTools();
    const currentUserQuery = useQuery<{ session: { currentUser: User } }>(
        GET_SESSION_CURRENT_USER,
    );
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    useEffect(() => {
        send({
            type: 'SET_REPORT_DESCRIPTION',
            value: {
                reportDescription: '',
            },
        });
    }, []);

    useEffect(() => {
        if (!hasStatements) {
            send({
                type: 'SET_LOADING',
                value: {
                    loading: false,
                },
            });

            send({
                type: 'UPDATE_STEP',
                value: {
                    stepId: 'selectPeriod',
                    update: {
                        statusMessage: null,
                        disableNextStep: true,
                    },
                },
            });
        }
    }, [hasStatements]);

    useEffect(() => {
        if (userId && selectedEntityId) {
            // Ensure that `periodSelections` are up to date
            fetchPeriodSelections(userId, selectedEntityId);
        }
    }, [userId, selectedEntityId]);

    // The selected period will be set by the ReportPeriodSelector, which also sets
    // `disableNextStep: false` and allows the user to proceed to the SetUp step.
    const performancePeriodLabel =
        selectedPeriod?.performance_period_label ?? null;

    const beforeNextStep = useCallback(async () => {
        if (!userId || !selectedEntityId || !performancePeriodLabel) {
            return false;
        }

        const token = await getTokenAsync();

        const success = await axios({
            method: 'post',
            url: trqkDirectoryUrl,
            headers: {
                Authorization: token,
            },
            data: {
                query: ADD_GAP_TO_TRQK_USER,
                variables: {
                    user_id: userId,
                    entity_id: selectedEntityId,
                    performance_period_label: performancePeriodLabel,
                },
            },
        })
            .then(
                (
                    response: AxiosResponse<{
                        data: {
                            addGapToTrqkUser: TrqkGap;
                        };
                    }>,
                ) => {
                    const newTrqkGap =
                        response?.data?.data?.addGapToTrqkUser ?? null;

                    if (!newTrqkGap) {
                        addToast({
                            kind: 'warning-banner',
                            content: locale.enUS.API_ERRORS.addGapToTrqkUser,
                            logMessage: 'addGapToTrqkUser',
                        });

                        return false;
                    }

                    send({
                        type: 'SET_TRQK_GAP',
                        value: {
                            trqkGap: newTrqkGap,
                        },
                    });

                    return true;
                },
            )
            .catch(() => {
                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.API_ERRORS.addGapToTrqkUser,
                    logMessage: 'addGapToTrqkUser',
                });

                return false;
            });

        return success;
    }, [userId, selectedEntityId, performancePeriodLabel]);

    return (
        <StepWrapper
            context={context}
            remove={remove}
            send={send}
            beforeNextStep={beforeNextStep}
        >
            {!hasStatements && (
                <div className="flex items-center justify-center h-full">
                    <div
                        className="grid"
                        style={{
                            gridTemplateColumns: '30px auto',
                        }}
                    >
                        <IconInfoCircle fillColor="primary-500" width={24} />
                        <p className="text-tertiary-400 italic">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .SELECT_PERIOD.NO_STATEMENTS_MESSAGE.PART_1
                            }{' '}
                            <button
                                type="button"
                                onClick={() => {
                                    remove({ canceled: true, success: true });

                                    setTimeout(() => {
                                        history.push({
                                            hash: HASHES.MANAGE_STATEMENTS
                                                .UPLOADS,
                                            search: history.location.search,
                                            pathname:
                                                FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                                        });
                                    }, 500);
                                }}
                                className="italic underline text-primary-500"
                            >
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .SELECT_PERIOD.NO_STATEMENTS_MESSAGE
                                        .PART_2
                                }
                            </button>{' '}
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .SELECT_PERIOD.NO_STATEMENTS_MESSAGE.PART_3
                            }
                        </p>
                    </div>
                </div>
            )}
            {hasStatements && (
                <div className="flex-grow flex flex-col justify-between">
                    <div className="space-y-4 text-sm">
                        <div>
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .SELECT_PERIOD.DESCRIPTION.PART_1
                            }{' '}
                            <Tooltip
                                addClasses="respond-to-mouse text-black"
                                messageClasses="respond-to-mouse"
                                background="light"
                                maxWidth={720}
                                minWidth={720}
                                RenderMessage={PerformanceVsStatementPeriods}
                                position="bottom"
                            >
                                <span className="text-primary-500 border-dashed border-b-2 border-primary-500">
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SELECT_PERIOD.DESCRIPTION.PART_2
                                    }
                                </span>
                            </Tooltip>
                            .
                        </div>
                        {canUploadDetections && (
                            <>
                                <p>
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SELECT_PERIOD.DESCRIPTION.PART_3
                                    }{' '}
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SELECT_PERIOD.DESCRIPTION.PART_4
                                    }
                                </p>
                            </>
                        )}
                        {!canUploadDetections && (
                            <>
                                <p>
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .SELECT_PERIOD.DESCRIPTION
                                            .PART_3_CANNOT_UPLOAD
                                    }
                                </p>
                            </>
                        )}
                    </div>
                    <div className="flex-grow flex items-start mt-6">
                        <ReportPeriodSelector
                            context={context}
                            send={send}
                            stepId={stepId}
                        />
                    </div>
                </div>
            )}
        </StepWrapper>
    );
};
